import BackButton from "components/BackButton";
import ConnectionStatus from "components/ConnectionStatus";
import BasicButton from "components/General/BasicButton";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWebSocket } from "WebSocketContext";
import { useVowGameContext } from "VowGameContext";

const VowJoin: React.FC = () => {
  const { playerId, socket } = useWebSocket();
  const { setGameId, setHostId } = useVowGameContext();
  const navigate = useNavigate();

  const [inviteCode, setInviteCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const latestCodeRef = useRef("");
  useEffect(() => {
    latestCodeRef.current = inviteCode;
  }, [inviteCode]);

  useEffect(() => {
    if (!socket) return;

    const handleMessage = (event: MessageEvent<string>) => {
      if (!event.data) return;
      let message: any;
      try {
        message = JSON.parse(event.data);
      } catch {
        return;
      }

      if (message?.kind === "ErrorMessage" && message?.message === "GAME NOT FOUND") {
        setErrorMessage("No games found with that code.");
      }

      if (message.kind === "JoinGameSuccess") {
        setGameId(latestCodeRef.current);
        setHostId(message.hostId);
        setErrorMessage("");
        navigate("/vow/select-role");
      }
    };

    socket.addEventListener("message", handleMessage);
    return () => {
      socket.removeEventListener("message", handleMessage);
    };
  }, [navigate, setGameId, setHostId, socket]);

  const requestJoin = () => {
    if (!inviteCode) {
      setErrorMessage("Please enter a valid invite code.");
      return;
    }
    if (playerId <= 0 || !socket) {
      setErrorMessage("Connection not ready. Please retry in a moment.");
      return;
    }

    setGameId(inviteCode);

    socket.send(JSON.stringify({
      kind: "RequestJoinGame",
      playerId,
      gameInviteCode: inviteCode,
    }));
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-[#f3eddb] px-4 py-10">
      <ConnectionStatus />
      <div className="relative w-full max-w-md border-2 border-black bg-[#BCB7A8] p-6">
        <BackButton useAbsolute={false} />
        <h1 className="mt-2 text-2xl font-semibold text-slate-900">Join a VOW Session</h1>
        <p className="mt-1 text-sm text-slate-800">
          Ask the host for the invite code and enter it below to join their game.
        </p>

        <div className="mt-4 flex flex-col gap-3">
          <label className="text-sm font-medium text-slate-800" htmlFor="invite-code">
            Invite Code
          </label>
          <input
            id="invite-code"
            type="text"
            value={inviteCode}
            onChange={(event) => setInviteCode(event.target.value.toUpperCase())}
            className="w-full border border-black bg-[#faf3e2] px-3 py-2 text-lg uppercase tracking-widest text-slate-900 outline-none focus:ring-2 focus:ring-black"
            placeholder="ABCD"
          />
          {errorMessage && (
            <p className="text-sm text-red-700">{errorMessage}</p>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <BasicButton
            text="Join Game"
            className="px-5 py-3"
            onClick={requestJoin}
          />
        </div>
      </div>
    </div>
  );
};

export default VowJoin;
